/**
 * The single source of truth for the visual scene (ADR-0008). Every change is a command; the
 * store keeps undo/redo history, coalescing continuous gestures (dragging a slider) into one step.
 */
import {
  applySceneCommand,
  type Scene,
  type SceneCommand,
  type TransitionSpec,
} from '../shared/scene';

export interface SceneChange {
  scene: Scene;
  /** Present when the whole scene was replaced (preset recall) and should transition in. */
  transition?: TransitionSpec;
  /** True when caused by undo/redo. */
  history?: boolean;
}

type Listener = (change: SceneChange) => void;

const MAX_HISTORY = 100;
const COALESCE_MS = 600;

export class SceneStore {
  private current: Scene;
  private readonly undoStack: Scene[] = [];
  private readonly redoStack: Scene[] = [];
  private readonly listeners = new Set<Listener>();
  private lastKey: string | null = null;
  private lastAt = 0;

  constructor(
    initial: Scene,
    private readonly now: () => number = () => performance.now(),
  ) {
    this.current = initial;
  }

  get scene(): Scene {
    return this.current;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * Apply a command. Commands with the same `coalesce` key arriving within a short window merge
   * into one undo step (a slider drag is one undo, not a hundred).
   */
  dispatch(cmd: SceneCommand, opts: { coalesce?: string } = {}): void {
    const next = applySceneCommand(this.current, cmd);
    if (next === this.current) return;
    const t = this.now();
    const merge =
      opts.coalesce !== undefined &&
      opts.coalesce === this.lastKey &&
      t - this.lastAt < COALESCE_MS;
    if (!merge) this.pushUndo(this.current);
    this.lastKey = opts.coalesce ?? null;
    this.lastAt = t;
    this.redoStack.length = 0;
    this.set(next);
  }

  /** Replace the whole scene (preset recall, import, share link), optionally with a transition. */
  replace(scene: Scene, transition?: TransitionSpec): void {
    this.pushUndo(this.current);
    this.redoStack.length = 0;
    this.lastKey = null;
    this.set(scene, transition);
  }

  undo(): void {
    const prev = this.undoStack.pop();
    if (!prev) return;
    this.redoStack.push(this.current);
    this.lastKey = null;
    this.set(prev, undefined, true);
  }

  redo(): void {
    const next = this.redoStack.pop();
    if (!next) return;
    this.undoStack.push(this.current);
    this.lastKey = null;
    this.set(next, undefined, true);
  }

  private pushUndo(scene: Scene): void {
    this.undoStack.push(scene);
    if (this.undoStack.length > MAX_HISTORY) this.undoStack.shift();
  }

  private set(scene: Scene, transition?: TransitionSpec, history = false): void {
    this.current = scene;
    const change: SceneChange = { scene };
    if (transition) change.transition = transition;
    if (history) change.history = true;
    for (const l of this.listeners) l(change);
  }
}
